import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, PlayCircle, ShoppingBag, DollarSign, TrendingUp, Eye, LogOut, Clock, Trash2, Loader2, MousePointerClick, Mail, MessageSquare, Globe, CheckCircle2, XCircle, AlertTriangle, Timer, BookOpen,
} from "lucide-react";
import Logo from "../components/Logo";
import { adminApi } from "../lib/api";
import { ReplyRescuePanel } from "../components/ReplyRescuePanel";
import { IPOPanel } from "../components/IPOPanel";
import { CoursePanel } from "../components/CoursePanel";
import { SevenPanel } from "../components/SevenPanel";
import { NabdPanel } from "../components/NabdPanel";
import { StripeFunnelPanel } from "../components/StripeFunnelPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("ar-EG").format(n);

const fmtDuration = (seconds) => {
  const s = Math.max(0, Math.round(seconds || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m === 0) return `${r} ث`;
  return `${m}:${String(r).padStart(2, "0")} د`;
};

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs tracking-[0.2em] text-[#A1B0C6]">{label}</span>
      <Icon className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5}/>
    </div>
    <div className="font-display text-3xl sm:text-4xl font-black text-white">{value}</div>
    {sub && <div className="text-xs text-[#A1B0C6] mt-2">{sub}</div>}
  </div>
);

const SegmentTabs = ({ segment, onSwitch }) => (
  <div className="max-w-7xl mx-auto mb-6" data-testid="segment-tabs">
    <div className="inline-flex items-center gap-1 p-1 bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm flex-wrap">
      {[
        { key: "ai", label: "AI Work Upgrade", emoji: "⚡", path: "bluebook.cx/" },
        { key: "thabit", label: "ثابت (Thabit)", emoji: "🎙️", path: "bluebook.cx/gulf" },
        { key: "reply", label: "Reply Rescue", emoji: "💬", path: "replyrescue.app" },
        { key: "ipo", label: "IPO Dossier", emoji: "📈", path: "replyrescue.app/ipoaccess" },
        { key: "course", label: "Performance Ads OS", emoji: "🎯", path: "replyrescue.app/perfads" },
        { key: "seven", label: "7 ليالي بيننا", emoji: "💑", path: "replyrescue.app/7days" },
        { key: "nabd", label: "نبض السوق AI", emoji: "📈", path: "replyrescue.app/nabd" },
        { key: "funnel", label: "Stripe Funnel", emoji: "📊", path: "drop-off analysis" },
      ].map((t) => (
        <button
          key={t.key}
          onClick={() => onSwitch(t.key)}
          data-testid={`segment-tab-${t.key}`}
          className={`px-5 py-2.5 text-sm font-bold rounded-sm transition-all ${
            segment === t.key
              ? t.key === "thabit"
                ? "bg-[#C5A059] text-[#040914]"
                : "bg-[#C9A227] text-[#0F172A]"
              : "text-[#A1B0C6] hover:text-white"
          }`}
        >
          <span className="ml-1.5">{t.emoji}</span>{t.label}
          <span className="block text-[10px] font-normal opacity-70 mt-0.5">{t.path}</span>
        </button>
      ))}
    </div>
    {segment === "ai" && (
      <div className="mt-2 text-xs text-[#C9A227]">
        بيانات صفحة <span className="font-bold">bluebook.cx/aicourse</span> — AI Work Upgrade · $49 USD · الدفع الداخلي عبر Stripe Checkout (USA)
      </div>
    )}
    {segment === "thabit" && (
      <div className="mt-2 text-xs text-[#C5A059]">
        بيانات صفحة <span className="font-bold">bluebook.cx/gulf</span> — ثابت (جلسات صوتية) · 199 ريال · الدفع عبر Stripe Payment Link
      </div>
    )}
    {segment === "reply" && (
      <div className="mt-2 text-xs text-[#C9A227]" style={{ direction: "ltr", textAlign: "left" }}>
        <span className="font-bold">replyrescue.app</span> — Reply Rescue · $9.99 / $19.99 / $14.99 · Stripe Checkout (LIVE)
      </div>
    )}
    {segment === "ipo" && (
      <div className="mt-2 text-xs text-[#3F8EFC]" style={{ direction: "ltr", textAlign: "left" }}>
        <span className="font-bold">replyrescue.app/ipoaccess</span> — IPO Access Dossier™ · $79 USD · Email-gated · Stripe Checkout (LIVE)
      </div>
    )}
    {segment === "course" && (
      <div className="mt-2 text-xs text-[#C9A66A]" style={{ direction: "ltr", textAlign: "left" }}>
        <span className="font-bold">replyrescue.app/perfads</span> — Performance Ads OS · 99 SAR · Email-gated · Stripe Checkout (LIVE)
      </div>
    )}
    {segment === "nabd" && (
      <div className="mt-2 text-xs text-[#F4B842]" style={{ direction: "ltr", textAlign: "left" }}>
        <span className="font-bold">replyrescue.app/nabd</span> — نبض السوق AI · 149 AED launch · Android APK + iOS App Store · Stripe Checkout (LIVE)
      </div>
    )}
    {segment === "funnel" && (
      <div className="mt-2 text-xs text-[#7DD3FC]" style={{ direction: "ltr", textAlign: "left" }}>
        <span className="font-bold">Stripe Checkout Funnel</span> — drop-off analysis across all paid funnels · helps explain abandoned sessions vs. failed payments
      </div>
    )}
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null);
  const [autoRefreshOn, setAutoRefreshOn] = useState(true);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [replyStats, setReplyStats] = useState(null);
  const [ipoStats, setIpoStats] = useState(null);
  const [ipoReconciling, setIpoReconciling] = useState(false);
  const [ipoReconcileResult, setIpoReconcileResult] = useState(null);
  const [courseStats, setCourseStats] = useState(null);
  const [courseReconciling, setCourseReconciling] = useState(false);
  const [courseReconcileResult, setCourseReconcileResult] = useState(null);
  const [sevenStats, setSevenStats] = useState(null);
  const [sevenReconciling, setSevenReconciling] = useState(false);
  const [sevenReconcileResult, setSevenReconcileResult] = useState(null);
  const [nabdStats, setNabdStats] = useState(null);
  const [nabdReconciling, setNabdReconciling] = useState(false);
  const [nabdReconcileResult, setNabdReconcileResult] = useState(null);
  // Segment tab: "ai" | "thabit" | "reply" | "ipo" | "course" | "seven" | "nabd"
  const [segment, setSegment] = useState(() => {
    const stored = localStorage.getItem("bb_admin_segment");
    return ["ai", "thabit", "reply", "ipo", "course", "seven", "nabd", "funnel"].includes(stored) ? stored : "ai";
  });

  const sendRecoveryEmails = async () => {
    const pendingWithEmail = orders.filter((o) => o.status !== "Paid" && o.email);
    if (pendingWithEmail.length === 0) {
      toast.info("لا توجد طلبات معلّقة تحتوي على بريد إلكتروني.");
      return;
    }
    const ok = window.confirm(
      `سيتم إرسال إيميل استرجاع لـ ${pendingWithEmail.length} عميل (لم يكملوا الدفع).\n\nهل تريد المتابعة؟`
    );
    if (!ok) return;
    setRecovering(true);
    try {
      const c = adminApi();
      const { data } = await c.post("/admin/recover-pending", {});
      toast.success(
        `أُرسل ${data.sent} إيميل · فشل ${data.failed} · تم تخطي ${data.skipped}`
      );
      await loadAll();
    } catch (e) {
      toast.error("تعذّر إرسال إيميلات الاسترجاع.");
    } finally {
      setRecovering(false);
    }
  };

  const reconcilePending = async ({ fix }) => {
    const label = fix
      ? "سيتم فحص كل الطلبات المعلّقة والمقارنة مع Stripe، وأي طلب دفع بنجاح سيتم تحويله إلى Paid تلقائياً. تأكيد؟"
      : "تحقق فقط (بدون تعديل) — مقارنة كل الطلبات المعلّقة مع Stripe لمعرفة سبب عدم اكتمال الدفع.";
    if (fix && !window.confirm(label)) return;
    setReconciling(true);
    setReconcileReport(null);
    try {
      const c = adminApi();
      const { data } = await c.post(`/admin/reconcile-pending?segment=${segment}&fix=${fix ? "true" : "false"}&limit=2000`);
      setReconcileReport(data);
      const s = data.summary || {};
      if (fix && (data.fixed_orders || []).length > 0) {
        toast.success(`تم استرداد ${data.fixed_orders.length} طلب (دفعوا فعلاً) وتحويلهم إلى Paid.`);
      } else {
        toast.success(`فحص ${s.total || 0} طلب · ${s.succeeded || 0} مدفوع غير مسجّل · ${s.no_pi || 0} لم يفتح الدفع`);
      }
      await loadAll();
    } catch (e) {
      toast.error("تعذّر تنفيذ المطابقة.");
    } finally {
      setReconciling(false);
    }
  };

  const loadAll = (seg = segment) => {
    const c = adminApi();
    if (seg === "reply") {
      return c.get("/admin/reply-stats").then((r) => {
        setReplyStats(r.data);
        setLastRefreshAt(new Date());
      });
    }
    if (seg === "ipo") {
      return c.get("/admin/ipo-stats").then((r) => {
        setIpoStats(r.data);
        setLastRefreshAt(new Date());
      });
    }
    if (seg === "course") {
      return c.get("/admin/course-stats").then((r) => {
        setCourseStats(r.data);
        setLastRefreshAt(new Date());
      });
    }
    if (seg === "seven") {
      return c.get("/admin/seven-stats").then((r) => {
        setSevenStats(r.data);
        setLastRefreshAt(new Date());
      });
    }
    if (seg === "nabd") {
      return c.get("/admin/nabd-stats").then((r) => {
        setNabdStats(r.data);
        setLastRefreshAt(new Date());
      });
    }
    if (seg === "funnel") {
      // StripeFunnelPanel manages its own data loading via /admin/stripe-funnel —
      // nothing to seed here. Return a resolved Promise so callers don't crash.
      return Promise.resolve();
    }
    return Promise.all([c.get(`/admin/stats?segment=${seg}`), c.get("/admin/orders")])
      .then(([s, o]) => {
        setStats(s.data);
        setOrders(o.data.orders || []);
        setLastRefreshAt(new Date());
      });
  };

  // Live auto-refresh — polls /admin/stats + /admin/orders every 30s so the
  // dashboard reflects new orders & CTA clicks during a campaign launch
  // without manual reload. Pauses while the tab is hidden to save quota.
  useEffect(() => {
    if (!autoRefreshOn) return;
    // Gate by whichever stats slot is relevant to the active segment so we
    // don't poll before the first load completes (avoids a flicker).
    const hasInitial = segment === "reply" ? !!replyStats
      : segment === "ipo" ? !!ipoStats
      : segment === "course" ? !!courseStats
      : segment === "seven" ? !!sevenStats
      : segment === "nabd" ? !!nabdStats
      : !!stats;
    if (!hasInitial) return;
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      loadAll(segment).catch(() => { /* silent — keep last snapshot */ });
    }, 30_000);
    return () => clearInterval(id);
  }, [autoRefreshOn, segment, stats, replyStats, ipoStats, courseStats, sevenStats, nabdStats]);

  useEffect(() => {
    const token = localStorage.getItem("bb_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    loadAll(segment).catch((e) => {
      // ONLY treat 401 (real auth failure) as a session expiry. Network blips,
      // 500s from a single broken endpoint, or transient errors should NOT
      // log the operator out — they were producing the "keeps logging me out"
      // symptom whenever any single admin endpoint had a problem.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setErr("انتهت الجلسة، يرجى تسجيل الدخول مجدداً.");
        localStorage.removeItem("bb_admin_token");
        setTimeout(() => navigate("/admin/login"), 1200);
      } else {
        setErr(`تعذّر تحميل البيانات (${status || "network"}) — يمكنكِ إعادة المحاولة من زر التحديث.`);
      }
    });
  }, [navigate, segment]);

  const switchSegment = (s) => {
    setSegment(s);
    localStorage.setItem("bb_admin_segment", s);
    setStats(null);
    setReplyStats(null);
    setIpoStats(null);
    setCourseStats(null);
    setSevenStats(null);
    setNabdStats(null);
  };

  const logout = () => {
    localStorage.removeItem("bb_admin_token");
    navigate("/admin/login");
  };

  const performReset = async () => {
    if (resetText !== "RESET") return;
    setResetting(true);
    try {
      const c = adminApi();
      if (segment === "reply") {
        const { data } = await c.post("/admin/reply-reset", { confirm: "RESET" });
        const total = Object.entries(data)
          .filter(([k]) => k.endsWith("_deleted"))
          .reduce((sum, [, v]) => sum + (v || 0), 0);
        toast.success(`Reply Rescue reset — ${total} records deleted`);
      } else if (segment === "ipo") {
        const { data } = await c.post("/admin/ipo-reset", { confirm: "RESET" });
        const total = Object.entries(data)
          .filter(([k]) => k.endsWith("_deleted"))
          .reduce((sum, [, v]) => sum + (v || 0), 0);
        toast.success(`IPO Dossier reset — ${total} records deleted`);
      } else if (segment === "course") {
        const { data } = await c.post("/admin/course-reset", { confirm: "RESET" });
        const total = Object.entries(data)
          .filter(([k]) => k.endsWith("_deleted"))
          .reduce((sum, [, v]) => sum + (v || 0), 0);
        toast.success(`Performance Ads OS reset — ${total} records deleted`);
      } else if (segment === "seven") {
        const { data } = await c.post("/admin/seven-reset", { confirm: "RESET" });
        const total = Object.entries(data)
          .filter(([k]) => k.endsWith("_deleted"))
          .reduce((sum, [, v]) => sum + (v || 0), 0);
        toast.success(`7 ليالي بيننا reset — ${total} records deleted`);
      } else if (segment === "nabd") {
        const { data } = await c.post("/admin/nabd-reset", { confirm: "RESET" });
        const total = Object.entries(data)
          .filter(([k]) => k.endsWith("_deleted"))
          .reduce((sum, [, v]) => sum + (v || 0), 0);
        toast.success(`نبض السوق AI reset — ${total} records deleted`);
      } else {
        // AI Work Upgrade OR Thabit — both use /admin/reset with scope.
        const scope = segment === "thabit" ? "thabit" : "ai";
        const projectName = scope === "thabit" ? "Thabit" : "AI Work Upgrade";
        const { data } = await c.post("/admin/reset", { confirm: "RESET", scope });
        const total = Object.entries(data)
          .filter(([k]) => k.endsWith("_deleted"))
          .reduce((sum, [, v]) => sum + (v || 0), 0);
        toast.success(`${projectName} reset — ${total} records deleted`);
      }
      setResetOpen(false);
      setResetText("");
      await loadAll();
    } catch (e) {
      toast.error(segment === "reply" || segment === "ipo" || segment === "course" || segment === "seven" || segment === "nabd" ? "Reset failed — try again." : "تعذّر تنفيذ عملية المسح، حاول مرة أخرى.");
    } finally {
      setResetting(false);
    }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-[#A1B0C6]">{err}</div>;

  if (segment === "reply") {
    return (
      <div className="min-h-screen bg-[#040914] text-white p-5 sm:p-8" dir="rtl">
        <header className="max-w-7xl mx-auto flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Logo className="h-12 w-12" />
            <div>
              <div className="text-[#C5A059] text-xs tracking-[0.3em]">لوحة التحكم</div>
              <h1 className="font-display text-xl font-bold">Reply Rescue</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefreshOn((v) => !v)}
              className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border transition-colors ${
                autoRefreshOn
                  ? "border-[#0F766E]/50 text-[#5EEAD4] hover:border-[#0F766E]"
                  : "border-[#A1B0C6]/30 text-[#A1B0C6] hover:border-[#A1B0C6]/60"
              }`}
              data-testid="admin-autorefresh-toggle"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefreshOn ? "bg-[#5EEAD4] animate-pulse" : "bg-[#A1B0C6]"}`} aria-hidden="true" />
              <span className="font-bold tracking-wider">{autoRefreshOn ? "LIVE · 30s" : "PAUSED"}</span>
            </button>
            <button
              onClick={() => setResetOpen(true)}
              className="flex items-center gap-2 text-sm text-red-400/80 hover:text-red-400 border border-red-500/30 hover:border-red-500/60 px-3 py-2 rounded-sm transition-colors"
              data-testid="reply-reset-btn"
            >
              <Trash2 className="w-4 h-4"/> Reset Reply Rescue
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-sm text-[#A1B0C6] hover:text-[#C5A059]"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4"/> خروج
            </button>
          </div>
        </header>
        <SegmentTabs segment={segment} onSwitch={switchSegment} />
        {replyStats
          ? <ReplyRescuePanel
              stats={replyStats}
              reconciling={reconciling}
              reconcileResult={reconcileReport}
              onReconcile={() => reconcilePending({ fix: true })}
            />
          : <div className="max-w-7xl mx-auto text-center text-[#A1B0C6] py-20">Loading…</div>}

        <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!resetting) { setResetOpen(o); if (!o) setResetText(""); } }}>
          <AlertDialogContent className="bg-[#0B1D3A] border border-red-500/40 text-white" dir="ltr" data-testid="reply-reset-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl text-left text-red-400">
                Warning — this cannot be undone
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left text-[#A1B0C6] leading-relaxed">
                This will permanently delete <span className="text-white font-bold">ALL Reply Rescue data</span>:
                <br/>sessions &amp; credits, payment records, generated messages, captured emails, funnel analytics, and checkout attempts.
                <br/><br/>
                <span className="text-red-400 font-bold">Buyers with active credits will lose access.</span>
                <br/><br/>
                To confirm, type <code className="bg-black/40 text-red-400 px-2 py-0.5 rounded-sm font-mono">RESET</code> below:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              value={resetText}
              onChange={(e) => setResetText(e.target.value)}
              placeholder="Type RESET here"
              className="bg-[#040914] border-red-500/40 text-white font-mono text-center tracking-widest"
              data-testid="reply-reset-confirm-input"
              disabled={resetting}
              autoFocus
            />
            <AlertDialogFooter className="gap-2 sm:gap-2">
              <AlertDialogCancel asChild disabled={resetting}>
                <Button variant="outline" className="bg-transparent border-[#C5A059]/30 text-[#A1B0C6] hover:bg-[#C5A059]/10 hover:text-white" data-testid="reply-reset-cancel-btn">
                  Cancel
                </Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild onClick={(e) => { e.preventDefault(); performReset(); }}>
                <Button
                  disabled={resetText !== "RESET" || resetting}
                  className="bg-red-500 hover:bg-red-600 text-white border-0 disabled:opacity-50"
                  data-testid="reply-reset-confirm-btn"
                >
                  {resetting ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> Resetting...</> : "Yes, delete everything"}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (segment === "ipo") {
    return (
      <div className="min-h-screen bg-[#040914] text-white p-5 sm:p-8" dir="rtl">
        <header className="max-w-7xl mx-auto flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Logo className="h-12 w-12" />
            <div>
              <div className="text-[#3F8EFC] text-xs tracking-[0.3em]">لوحة التحكم</div>
              <h1 className="font-display text-xl font-bold">IPO Access Dossier</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefreshOn((v) => !v)}
              className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border transition-colors ${
                autoRefreshOn
                  ? "border-[#0F766E]/50 text-[#5EEAD4] hover:border-[#0F766E]"
                  : "border-[#A1B0C6]/30 text-[#A1B0C6] hover:border-[#A1B0C6]/60"
              }`}
              data-testid="admin-autorefresh-toggle"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefreshOn ? "bg-[#5EEAD4] animate-pulse" : "bg-[#A1B0C6]"}`} aria-hidden="true" />
              <span className="font-bold tracking-wider">{autoRefreshOn ? "LIVE · 30s" : "PAUSED"}</span>
            </button>
            <button
              onClick={() => setResetOpen(true)}
              className="flex items-center gap-2 text-sm text-red-400/80 hover:text-red-400 border border-red-500/30 hover:border-red-500/60 px-3 py-2 rounded-sm transition-colors"
              data-testid="ipo-reset-btn"
            >
              <Trash2 className="w-4 h-4"/> Reset IPO Dossier
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-sm text-[#A1B0C6] hover:text-[#3F8EFC]"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4"/> خروج
            </button>
          </div>
        </header>
        <SegmentTabs segment={segment} onSwitch={switchSegment} />
        {ipoStats
          ? <IPOPanel
              stats={ipoStats}
              reconciling={ipoReconciling}
              reconcileResult={ipoReconcileResult}
              onReconcile={async () => {
                setIpoReconciling(true);
                try {
                  const { data } = await adminApi().post("/admin/ipo-reconcile");
                  setIpoReconcileResult(data);
                  const fixed = data?.summary?.fixed || 0;
                  if (fixed > 0) toast.success(`Reconciled — ${fixed} order${fixed === 1 ? "" : "s"} flipped to paid`);
                  else toast.success(`Reconciled ${data?.summary?.checked || 0} unpaid order(s) — none changed status`);
                  // Refresh stats so the panel reflects flipped orders
                  await loadAll("ipo").catch(() => {});
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Reconcile failed — try again");
                } finally {
                  setIpoReconciling(false);
                }
              }}
            />
          : <div className="max-w-7xl mx-auto text-center text-[#A1B0C6] py-20">Loading…</div>}

        <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!resetting) { setResetOpen(o); if (!o) setResetText(""); } }}>
          <AlertDialogContent className="bg-[#0B1D3A] border border-red-500/40 text-white" dir="ltr" data-testid="ipo-reset-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl text-left text-red-400">
                Warning — this cannot be undone
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left text-[#A1B0C6] leading-relaxed">
                This will permanently delete <span className="text-white font-bold">ALL IPO Access Dossier data</span>:
                <br/>orders (paid &amp; unpaid), download history, and funnel analytics.
                <br/><br/>
                <span className="text-red-400 font-bold">Buyers will lose access to their download link.</span>
                <br/><br/>
                To confirm, type <code className="bg-black/40 text-red-400 px-2 py-0.5 rounded-sm font-mono">RESET</code> below:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              value={resetText}
              onChange={(e) => setResetText(e.target.value)}
              placeholder="Type RESET here"
              className="bg-[#040914] border-red-500/40 text-white font-mono text-center tracking-widest"
              data-testid="ipo-reset-confirm-input"
              disabled={resetting}
              autoFocus
            />
            <AlertDialogFooter className="gap-2 sm:gap-2">
              <AlertDialogCancel asChild disabled={resetting}>
                <Button variant="outline" className="bg-transparent border-[#3F8EFC]/30 text-[#A1B0C6] hover:bg-[#3F8EFC]/10 hover:text-white" data-testid="ipo-reset-cancel-btn">
                  Cancel
                </Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild onClick={(e) => { e.preventDefault(); performReset(); }}>
                <Button
                  disabled={resetText !== "RESET" || resetting}
                  className="bg-red-500 hover:bg-red-600 text-white border-0 disabled:opacity-50"
                  data-testid="ipo-reset-confirm-btn"
                >
                  {resetting ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> Resetting...</> : "Yes, delete everything"}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (segment === "course") {
    return (
      <div className="min-h-screen bg-[#040914] text-white p-5 sm:p-8" dir="rtl">
        <header className="max-w-7xl mx-auto flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Logo className="h-12 w-12" />
            <div>
              <div className="text-[#C9A66A] text-xs tracking-[0.3em]">لوحة التحكم</div>
              <h1 className="font-display text-xl font-bold">Performance Ads OS</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefreshOn((v) => !v)}
              className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border transition-colors ${
                autoRefreshOn
                  ? "border-[#0F766E]/50 text-[#5EEAD4] hover:border-[#0F766E]"
                  : "border-[#A1B0C6]/30 text-[#A1B0C6] hover:border-[#A1B0C6]/60"
              }`}
              data-testid="admin-autorefresh-toggle"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefreshOn ? "bg-[#5EEAD4] animate-pulse" : "bg-[#A1B0C6]"}`} aria-hidden="true" />
              <span className="font-bold tracking-wider">{autoRefreshOn ? "LIVE · 30s" : "PAUSED"}</span>
            </button>
            <button
              onClick={() => setResetOpen(true)}
              className="flex items-center gap-2 text-sm text-red-400/80 hover:text-red-400 border border-red-500/30 hover:border-red-500/60 px-3 py-2 rounded-sm transition-colors"
              data-testid="course-reset-btn"
            >
              <Trash2 className="w-4 h-4"/> Reset Performance Ads OS
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-sm text-[#A1B0C6] hover:text-[#C9A66A]"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4"/> خروج
            </button>
          </div>
        </header>
        <SegmentTabs segment={segment} onSwitch={switchSegment} />
        {courseStats
          ? <CoursePanel
              stats={courseStats}
              reconciling={courseReconciling}
              reconcileResult={courseReconcileResult}
              onReconcile={async () => {
                setCourseReconciling(true);
                try {
                  const { data } = await adminApi().post("/admin/course-reconcile");
                  setCourseReconcileResult(data);
                  const fixed = data?.summary?.fixed || 0;
                  if (fixed > 0) toast.success(`Reconciled — ${fixed} order${fixed === 1 ? "" : "s"} flipped to paid`);
                  else toast.success(`Reconciled ${data?.summary?.checked || 0} unpaid order(s) — none changed status`);
                  await loadAll("course").catch(() => {});
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Reconcile failed — try again");
                } finally {
                  setCourseReconciling(false);
                }
              }}
            />
          : <div className="max-w-7xl mx-auto text-center text-[#A1B0C6] py-20">Loading…</div>}

        <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!resetting) { setResetOpen(o); if (!o) setResetText(""); } }}>
          <AlertDialogContent className="bg-[#0B1D3A] border border-red-500/40 text-white" dir="ltr" data-testid="course-reset-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl text-left text-red-400">
                Warning — this cannot be undone
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left text-[#A1B0C6] leading-relaxed">
                This will permanently delete <span className="text-white font-bold">ALL Performance Ads OS data</span>:
                <br/>orders (paid &amp; unpaid), platform survey answers, and funnel analytics.
                <br/><br/>
                <span className="text-red-400 font-bold">Buyers will lose access to their download confirmation page.</span> (MEGA link in their email still works.)
                <br/><br/>
                To confirm, type <code className="bg-black/40 text-red-400 px-2 py-0.5 rounded-sm font-mono">RESET</code> below:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              value={resetText}
              onChange={(e) => setResetText(e.target.value)}
              placeholder="Type RESET here"
              className="bg-[#040914] border-red-500/40 text-white font-mono text-center tracking-widest"
              data-testid="course-reset-confirm-input"
              disabled={resetting}
              autoFocus
            />
            <AlertDialogFooter className="gap-2 sm:gap-2">
              <AlertDialogCancel asChild disabled={resetting}>
                <Button variant="outline" className="bg-transparent border-[#C9A66A]/30 text-[#A1B0C6] hover:bg-[#C9A66A]/10 hover:text-white" data-testid="course-reset-cancel-btn">
                  Cancel
                </Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild onClick={(e) => { e.preventDefault(); performReset(); }}>
                <Button
                  disabled={resetText !== "RESET" || resetting}
                  className="bg-red-500 hover:bg-red-600 text-white border-0 disabled:opacity-50"
                  data-testid="course-reset-confirm-btn"
                >
                  {resetting ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> Resetting...</> : "Yes, delete everything"}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (segment === "seven") {
    return (
      <div className="min-h-screen bg-[#0A0606] text-white p-5 sm:p-8" dir="rtl">
        <header className="max-w-7xl mx-auto flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src="/images/7nights-logo.png" alt="" className="h-11 w-11 object-contain" />
            <div>
              <div className="text-[#C9A66A] text-xs tracking-[0.3em]">لوحة التحكم</div>
              <h1 className="font-display text-xl font-bold">7 ليالي بيننا</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setAutoRefreshOn((v) => !v)} className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border transition-colors ${autoRefreshOn ? "border-[#0F766E]/50 text-[#5EEAD4] hover:border-[#0F766E]" : "border-[#A1B0C6]/30 text-[#A1B0C6] hover:border-[#A1B0C6]/60"}`} data-testid="admin-autorefresh-toggle">
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefreshOn ? "bg-[#5EEAD4] animate-pulse" : "bg-[#A1B0C6]"}`} aria-hidden="true" />
              <span className="font-bold tracking-wider">{autoRefreshOn ? "LIVE · 30s" : "PAUSED"}</span>
            </button>
            <button onClick={() => setResetOpen(true)} className="flex items-center gap-2 text-sm text-red-400/80 hover:text-red-400 border border-red-500/30 hover:border-red-500/60 px-3 py-2 rounded-sm transition-colors" data-testid="seven-reset-btn">
              <Trash2 className="w-4 h-4"/> Reset 7 ليالي بيننا
            </button>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-[#A1B0C6] hover:text-[#C9A66A]" data-testid="admin-logout">
              <LogOut className="w-4 h-4"/> خروج
            </button>
          </div>
        </header>
        <SegmentTabs segment={segment} onSwitch={switchSegment} />
        {sevenStats
          ? <SevenPanel
              stats={sevenStats}
              reconciling={sevenReconciling}
              reconcileResult={sevenReconcileResult}
              onReconcile={async () => {
                setSevenReconciling(true);
                try {
                  const { data } = await adminApi().post("/admin/seven-reconcile");
                  setSevenReconcileResult(data);
                  const fixed = data?.summary?.fixed || 0;
                  if (fixed > 0) toast.success(`Reconciled — ${fixed} order${fixed === 1 ? "" : "s"} flipped to paid`);
                  else toast.success(`Reconciled ${data?.summary?.checked || 0} unpaid order(s) — none changed status`);
                  await loadAll("seven").catch(() => {});
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Reconcile failed — try again");
                } finally {
                  setSevenReconciling(false);
                }
              }}
            />
          : <div className="max-w-7xl mx-auto text-center text-[#A1B0C6] py-20">Loading…</div>}

        <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!resetting) { setResetOpen(o); if (!o) setResetText(""); } }}>
          <AlertDialogContent className="bg-[#150C0C] border border-red-500/40 text-white" dir="ltr" data-testid="seven-reset-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl text-left text-red-400">Warning — this cannot be undone</AlertDialogTitle>
              <AlertDialogDescription className="text-left text-[#A1B0C6] leading-relaxed">
                This will permanently delete <span className="text-white font-bold">all 7 ليالي بيننا data</span>:
                <br/>orders (paid & unpaid), funnel analytics, bundle attempts.
                <br/><br/>
                <span className="text-red-400 font-bold">Buyers will lose access to their dashboard confirmation page.</span> (Their email with dashboard link still works.)
                <br/><br/>
                Type <code className="bg-black/40 text-red-400 px-2 py-0.5 rounded-sm font-mono">RESET</code> below to confirm:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="Type RESET here" className="bg-[#0A0606] border-red-500/40 text-white font-mono text-center tracking-widest" data-testid="seven-reset-confirm-input" disabled={resetting} autoFocus />
            <AlertDialogFooter className="gap-2 sm:gap-2">
              <AlertDialogCancel asChild disabled={resetting}>
                <Button variant="outline" className="bg-transparent border-[#C9A66A]/30 text-[#A1B0C6] hover:bg-[#C9A66A]/10 hover:text-white" data-testid="seven-reset-cancel-btn">Cancel</Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild onClick={(e) => { e.preventDefault(); performReset(); }}>
                <Button disabled={resetText !== "RESET" || resetting} className="bg-red-500 hover:bg-red-600 text-white border-0 disabled:opacity-50" data-testid="seven-reset-confirm-btn">
                  {resetting ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> Resetting...</> : "Yes, delete everything"}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (segment === "nabd") {
    return (
      <div className="min-h-screen bg-[#06090F] text-white p-5 sm:p-8" dir="rtl">
        <header className="max-w-7xl mx-auto flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ background: "linear-gradient(180deg,#0E141C,#040608)", borderColor: "rgba(244,184,66,0.40)" }}>
              <PlayCircle className="w-5 h-5 text-[#F4B842]" strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-[#F4B842] text-xs tracking-[0.3em]">لوحة التحكم</div>
              <h1 className="font-display text-xl font-bold">نبض السوق AI</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefreshOn((v) => !v)}
              className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border transition-colors ${
                autoRefreshOn
                  ? "border-[#0F766E]/50 text-[#5EEAD4] hover:border-[#0F766E]"
                  : "border-[#A1B0C6]/30 text-[#A1B0C6] hover:border-[#A1B0C6]/60"
              }`}
              data-testid="admin-autorefresh-toggle"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefreshOn ? "bg-[#5EEAD4] animate-pulse" : "bg-[#A1B0C6]"}`} aria-hidden="true" />
              <span className="font-bold tracking-wider">{autoRefreshOn ? "LIVE · 30s" : "PAUSED"}</span>
            </button>
            <button
              onClick={() => setResetOpen(true)}
              className="flex items-center gap-2 text-sm text-red-400/80 hover:text-red-400 border border-red-500/30 hover:border-red-500/60 px-3 py-2 rounded-sm transition-colors"
              data-testid="nabd-reset-btn"
            >
              <Trash2 className="w-4 h-4"/> Reset نبض السوق AI
            </button>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-[#A1B0C6] hover:text-[#F4B842]" data-testid="admin-logout">
              <LogOut className="w-4 h-4"/> خروج
            </button>
          </div>
        </header>
        <SegmentTabs segment={segment} onSwitch={switchSegment} />
        {nabdStats
          ? <NabdPanel
              stats={nabdStats}
              reconciling={nabdReconciling}
              reconcileResult={nabdReconcileResult}
              onReconcile={async () => {
                setNabdReconciling(true);
                try {
                  const { data } = await adminApi().post("/admin/nabd-reconcile");
                  setNabdReconcileResult(data);
                  const fixed = data?.summary?.fixed || 0;
                  if (fixed > 0) toast.success(`Reconciled — ${fixed} order${fixed === 1 ? "" : "s"} flipped to paid`);
                  else toast.success(`Reconciled ${data?.summary?.checked || 0} unpaid order(s) — none changed status`);
                  await loadAll("nabd").catch(() => {});
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Reconcile failed — try again");
                } finally {
                  setNabdReconciling(false);
                }
              }}
            />
          : <div className="max-w-7xl mx-auto text-center text-[#A1B0C6] py-20">Loading…</div>}

        <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!resetting) { setResetOpen(o); if (!o) setResetText(""); } }}>
          <AlertDialogContent className="bg-[#0B1018] border border-red-500/40 text-white" dir="ltr" data-testid="nabd-reset-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl text-left text-red-400">Warning — this cannot be undone</AlertDialogTitle>
              <AlertDialogDescription className="text-left text-[#A1B0C6] leading-relaxed">
                This will permanently delete <span className="text-white font-bold">all نبض السوق AI data</span>:
                <br/>orders (paid &amp; unpaid), funnel analytics, fulfilment events (Android downloads + iOS opens).
                <br/><br/>
                <span className="text-red-400 font-bold">Buyers will lose their success-page download link record.</span>
                <br/><br/>
                Type <code className="bg-black/40 text-red-400 px-2 py-0.5 rounded-sm font-mono">RESET</code> below to confirm:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="Type RESET here" className="bg-[#040608] border-red-500/40 text-white font-mono text-center tracking-widest" data-testid="nabd-reset-confirm-input" disabled={resetting} autoFocus />
            <AlertDialogFooter className="gap-2 sm:gap-2">
              <AlertDialogCancel asChild disabled={resetting}>
                <Button variant="outline" className="bg-transparent border-[#F4B842]/30 text-[#A1B0C6] hover:bg-[#F4B842]/10 hover:text-white" data-testid="nabd-reset-cancel-btn">Cancel</Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild onClick={(e) => { e.preventDefault(); performReset(); }}>
                <Button disabled={resetText !== "RESET" || resetting} className="bg-red-500 hover:bg-red-600 text-white border-0 disabled:opacity-50" data-testid="nabd-reset-confirm-btn">
                  {resetting ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> Resetting...</> : "Yes, delete everything"}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (segment === "funnel") {
    return (
      <div className="min-h-screen bg-[#040914] text-white p-5 sm:p-8" dir="rtl">
        <header className="max-w-7xl mx-auto flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Logo className="h-12 w-12" />
            <div>
              <div className="text-[#C5A059] text-xs tracking-[0.3em]">لوحة التحكم</div>
              <h1 className="font-display text-xl font-bold">Stripe Checkout Funnel</h1>
            </div>
          </div>
          <button
            onClick={() => { localStorage.removeItem("admin_token"); navigate("/admin/login"); }}
            className="text-[#A1B0C6] hover:text-white inline-flex items-center gap-2 text-xs"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> تسجيل الخروج
          </button>
        </header>
        <SegmentTabs segment={segment} onSwitch={switchSegment} />
        <StripeFunnelPanel />
      </div>
    );
  }

  if (!stats) return <div className="min-h-screen flex items-center justify-center text-[#A1B0C6]">جاري التحميل...</div>;

  return (
    <div className="min-h-screen bg-[#040914] text-white p-5 sm:p-8" dir="rtl">
      <header className="max-w-7xl mx-auto flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Logo className="h-12 w-12" />
          <div>
            <div className="text-[#C5A059] text-xs tracking-[0.3em]">لوحة التحكم</div>
            <h1 className="font-display text-xl font-bold">إحصائيات الكتاب الأزرق</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefreshOn((v) => !v)}
            className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border transition-colors ${
              autoRefreshOn
                ? "border-[#0F766E]/50 text-[#5EEAD4] hover:border-[#0F766E]"
                : "border-[#A1B0C6]/30 text-[#A1B0C6] hover:border-[#A1B0C6]/60"
            }`}
            data-testid="admin-autorefresh-toggle"
            title={autoRefreshOn ? "إيقاف التحديث التلقائي" : "تشغيل التحديث التلقائي"}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${autoRefreshOn ? "bg-[#5EEAD4] animate-pulse" : "bg-[#A1B0C6]"}`}
              aria-hidden="true"
            />
            <span className="font-bold tracking-wider">
              {autoRefreshOn ? "LIVE · 30s" : "PAUSED"}
            </span>
            {lastRefreshAt && (
              <span className="text-[10px] text-[#A1B0C6]/70 ml-1" style={{ direction: "ltr" }}>
                {lastRefreshAt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
          </button>
          <button
            onClick={sendRecoveryEmails}
            disabled={recovering}
            className="flex items-center gap-2 text-sm text-[#C5A059] hover:text-[#E5C07B] border border-[#C5A059]/40 hover:border-[#C5A059] px-3 py-2 rounded-sm transition-colors disabled:opacity-60"
            data-testid="admin-recover-btn"
          >
            {recovering ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4"/>}
            إرسال إيميلات الاسترجاع
          </button>
          <button
            onClick={() => setResetOpen(true)}
            className="flex items-center gap-2 text-sm text-red-400/80 hover:text-red-400 border border-red-500/30 hover:border-red-500/60 px-3 py-2 rounded-sm transition-colors"
            data-testid="admin-reset-btn"
          >
            <Trash2 className="w-4 h-4"/> {segment === "thabit" ? "Reset Thabit" : "Reset AI Work Upgrade"}
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-sm text-[#A1B0C6] hover:text-[#C5A059]"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4"/> خروج
          </button>
        </div>
      </header>

      {/* Product segment tabs */}
      <SegmentTabs segment={segment} onSwitch={switchSegment} />

      {/* Top KPI cards — segment-aware.
          AI uses internal Stripe Checkout ($49 USD): show visits → paid orders → revenue → conversion.
          Thabit uses external Stripe Payment Link: show visits → link clicks → conversion → time on site. */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8">
        {segment === "ai" ? (
          <>
            <Stat
              icon={Users}
              label="زيارات الصفحة"
              value={fmt(stats.visits)}
              sub="bluebook.cx/aicourse"
              testid="stat-visits"
            />
            <Stat
              icon={ShoppingBag}
              label="طلبات مدفوعة"
              value={fmt(stats.paid_orders || 0)}
              sub={`${fmt(stats.total_orders || 0)} طلب أُنشئ · ${fmt(stats.failed_orders || 0)} فشل`}
              testid="stat-ai-paid-orders"
            />
            <Stat
              icon={DollarSign}
              label="الإيرادات"
              value={`$${fmt(Math.round(stats.revenue || 0))}`}
              sub={`USD · $49 × ${fmt(stats.paid_orders || 0)} طلب`}
              testid="stat-ai-revenue"
            />
            <Stat
              icon={TrendingUp}
              label="معدّل التحويل"
              value={(() => {
                const v = stats.visits || 0;
                const p = stats.paid_orders || 0;
                return v ? `${((p / v) * 100).toFixed(2)}%` : "—";
              })()}
              sub="paid / visits"
              testid="stat-ai-conversion"
            />
            <Stat
              icon={MousePointerClick}
              label="نقرات أزرار الشراء"
              value={fmt(stats.cta_clicks || 0)}
              sub="إجمالي نقرات CTA على الصفحة"
              testid="stat-ai-cta"
            />
            <Stat
              icon={ShoppingBag}
              label="معدّل تحويل CTA → دفع"
              value={(() => {
                const c = stats.cta_clicks || 0;
                const p = stats.paid_orders || 0;
                return c ? `${((p / c) * 100).toFixed(1)}%` : "—";
              })()}
              sub="paid / cta clicks"
              testid="stat-ai-cta-to-paid"
            />
            <Stat
              icon={Mail}
              label="طلبات بإيميل اختياري"
              value={fmt((orders || []).filter((o) => (o.package === "ai_work_upgrade" || o.is_ai) && o.email).length)}
              sub="عملاء أضافوا بريد"
              testid="stat-ai-with-email"
            />
            <Stat
              icon={Clock}
              label="متوسط الوقت في الصفحة"
              value={fmtDuration(stats.avg_time_on_site_seconds)}
              sub={`من ${fmt(stats.total_sessions || 0)} جلسة`}
              testid="stat-time-on-site"
            />
          </>
        ) : (
          <>
            <Stat
              icon={Users}
              label="زيارات الصفحة"
              value={fmt(stats.visits)}
              sub="bluebook.cx/gulf"
              testid="stat-visits"
            />
            <Stat
              icon={MousePointerClick}
              label="نقرات Stripe Payment Link"
              value={fmt(stats.thabit_stripe_link_total || 0)}
              sub="نية شراء — ضغطوا للذهاب لـ Stripe"
              testid="stat-stripe-clicks"
            />
            <Stat
              icon={TrendingUp}
              label="معدّل التحويل للدفع"
              value={(() => {
                const v = stats.visits || 0;
                const c = stats.thabit_stripe_link_total || 0;
                return v ? `${((c / v) * 100).toFixed(1)}%` : "—";
              })()}
              sub="نسبة الزائرين الذين ضغطوا Stripe"
              testid="stat-stripe-conversion"
            />
            <Stat
              icon={Clock}
              label="متوسط الوقت في الموقع"
              value={fmtDuration(stats.avg_time_on_site_seconds)}
              sub={`من ${fmt(stats.total_sessions || 0)} جلسة`}
              testid="stat-time-on-site"
            />
          </>
        )}
      </div>

      {/* AI Work Upgrade — DEPRECATED Stripe Payment Link section.
          AI now uses internal Stripe Checkout, so we keep this only as a historical
          fallback (renders only if old data still exists in DB). */}
      {segment === "ai" && false && (stats.ai_stripe_link_total || 0) > 0 && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C9A227]/40 rounded-sm p-5 mb-8"
          data-testid="ai-stripe-link-section"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-lg">
                <span className="text-[#C9A227]">AI Work Upgrade</span> — زيارات صفحة الدفع (Stripe)
              </h2>
              <div className="text-[11px] text-[#A1B0C6] mt-1">
                عدد النقرات على أزرار الشراء التي حوّلت المستخدم إلى صفحة Stripe Payment Link
              </div>
            </div>
            <div className="text-left">
              <div className="font-display text-3xl font-black text-[#C9A227]" data-testid="ai-stripe-link-total">
                {fmt(stats.ai_stripe_link_total || 0)}
              </div>
              <div className="text-[10px] text-[#A1B0C6] tracking-widest">إجمالي النقرات</div>
            </div>
          </div>

          {/* Breakdown by CTA placement */}
          {Array.isArray(stats.ai_stripe_link_breakdown) && stats.ai_stripe_link_breakdown.length > 0 && (
            <div>
              <div className="text-xs text-[#A1B0C6] tracking-[0.2em] mb-2 uppercase">حسب موقع الزر</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                {stats.ai_stripe_link_breakdown.map((row) => {
                  const niceLabel = ({
                    "buy-ai-hero": "Hero (أعلى الصفحة)",
                    "buy-ai-why-different": "Why Different",
                    "buy-ai-final": "Final CTA (نهاية الصفحة)",
                    "buy-ai-sticky": "Sticky (موبايل)",
                  })[row.label] || row.label;
                  return (
                    <div
                      key={row.label}
                      className="bg-[#040914]/60 border border-[#C9A227]/20 rounded-sm p-3 text-center"
                      data-testid={`ai-stripe-row-${row.label}`}
                    >
                      <div className="font-display text-2xl font-black text-[#C9A227]">{fmt(row.count)}</div>
                      <div className="text-[11px] text-[#A1B0C6] mt-1">{niceLabel}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Breakdown by country */}
          {Array.isArray(stats.ai_stripe_link_country) && stats.ai_stripe_link_country.length > 0 && (
            <div>
              <div className="text-xs text-[#A1B0C6] tracking-[0.2em] mb-2 uppercase">حسب الدولة</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
                {stats.ai_stripe_link_country.map((row) => (
                  <div
                    key={row.country}
                    className="bg-[#040914]/60 border border-[#C9A227]/15 rounded-sm p-2 text-center"
                    data-testid={`ai-stripe-country-${row.country}`}
                  >
                    <div className="font-display text-lg font-black text-[#C9A227]">{fmt(row.count)}</div>
                    <div className="text-[10px] text-[#A1B0C6] mt-0.5 tracking-wider">{row.country}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Thabit (ثابت) — Stripe Payment Link outbound clicks */}
      {segment === "thabit" && (stats.thabit_stripe_link_total || 0) > 0 && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C5A059]/40 rounded-sm p-5 mb-8"
          data-testid="thabit-stripe-link-section"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-lg">
                <span className="text-[#C5A059]">ثابت (Thabit)</span> — زيارات صفحة الدفع (Stripe)
              </h2>
              <div className="text-[11px] text-[#A1B0C6] mt-1">
                عدد النقرات على أزرار الشراء التي حوّلت المستخدم إلى صفحة Stripe Payment Link من /gulf
              </div>
            </div>
            <div className="text-left">
              <div className="font-display text-3xl font-black text-[#C5A059]" data-testid="thabit-stripe-link-total">
                {fmt(stats.thabit_stripe_link_total || 0)}
              </div>
              <div className="text-[10px] text-[#A1B0C6] tracking-widest">إجمالي النقرات</div>
            </div>
          </div>

          {/* Breakdown by CTA placement */}
          {Array.isArray(stats.thabit_stripe_link_breakdown) && stats.thabit_stripe_link_breakdown.length > 0 && (
            <div>
              <div className="text-xs text-[#A1B0C6] tracking-[0.2em] mb-2 uppercase">حسب موقع الزر</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                {stats.thabit_stripe_link_breakdown.map((row) => {
                  const niceLabel = ({
                    "buy-thabit-header": "Header (أعلى الصفحة)",
                    "buy-thabit-hero": "Hero",
                    "buy-thabit-middle": "Middle (قسم المنتج)",
                    "buy-thabit-pricing": "Pricing (قسم السعر)",
                    "buy-thabit-sticky": "Sticky (موبايل)",
                    "buy-thabit-video-modal": "Video Modal (مقدمة ثابت)",
                    "buy-thabit-preview-modal": "Preview Modal",
                    "buy-thabit-chat": "Chat Bot (المساعد)",
                  })[row.label] || row.label;
                  return (
                    <div
                      key={row.label}
                      className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-3 text-center"
                      data-testid={`thabit-stripe-row-${row.label}`}
                    >
                      <div className="font-display text-2xl font-black text-[#C5A059]">{fmt(row.count)}</div>
                      <div className="text-[11px] text-[#A1B0C6] mt-1">{niceLabel}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Breakdown by country */}
          {Array.isArray(stats.thabit_stripe_link_country) && stats.thabit_stripe_link_country.length > 0 && (
            <div>
              <div className="text-xs text-[#A1B0C6] tracking-[0.2em] mb-2 uppercase">حسب الدولة</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
                {stats.thabit_stripe_link_country.map((row) => (
                  <div
                    key={row.country}
                    className="bg-[#040914]/60 border border-[#C5A059]/15 rounded-sm p-2 text-center"
                    data-testid={`thabit-stripe-country-${row.country}`}
                  >
                    <div className="font-display text-lg font-black text-[#C5A059]">{fmt(row.count)}</div>
                    <div className="text-[10px] text-[#A1B0C6] mt-0.5 tracking-wider">{row.country}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* AI Work Upgrade — CTA placements & funnel to paid */}
      {segment === "ai" && (() => {
        const rows = Array.isArray(stats.cta_breakdown) ? stats.cta_breakdown : [];
        const aiRows = rows.filter((r) => (r.label || "").startsWith("buy-ai-"));
        if (aiRows.length === 0) return null;
        const niceLabel = {
          "buy-ai-primary": "Primary (افتراضي)",
          "buy-ai-hero": "Hero (أعلى الصفحة)",
          "buy-ai-what-you-get": "What You Get (أول CTA شراء)",
          "buy-ai-value": "Value (قسم لماذا مختلف)",
          "buy-ai-final": "Final CTA (نهاية الصفحة)",
          "buy-ai-sticky": "Sticky (موبايل)",
          "buy-ai-desktop-sticky": "Sticky (ديسكتوب)",
          "ai-hero-see-inside": "Hero — See What's Inside (low commitment)",
        };
        return (
          <section
            className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C9A227]/40 rounded-sm p-5 mb-8"
            data-testid="ai-cta-placement-section"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-lg">
                  <span className="text-[#C9A227]">AI Work Upgrade</span> — نقرات أزرار الشراء حسب الموقع
                </h2>
                <div className="text-[11px] text-[#A1B0C6] mt-1">
                  كل نقرة تنشئ طلب Pending وتحوّل المستخدم إلى صفحة /checkout الداخلية
                </div>
              </div>
              <div className="text-left">
                <div className="font-display text-3xl font-black text-[#C9A227]" data-testid="ai-cta-total">
                  {fmt(aiRows.reduce((s, r) => s + (r.count || 0), 0))}
                </div>
                <div className="text-[10px] text-[#A1B0C6] tracking-widest">إجمالي النقرات</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {aiRows.map((row) => (
                <div
                  key={row.label}
                  className="bg-[#040914]/60 border border-[#C9A227]/20 rounded-sm p-3 text-center"
                  data-testid={`ai-cta-row-${row.label}`}
                >
                  <div className="font-display text-2xl font-black text-[#C9A227]">{fmt(row.count)}</div>
                  <div className="text-[11px] text-[#A1B0C6] mt-1">{niceLabel[row.label] || row.label}</div>
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Pending Payment Reconciliation — checks every Pending order against
          Stripe's PaymentIntent and surfaces (a) silent paid sales we never
          flipped, (b) decline-code distribution, (c) abandonment buckets.
          Critical tool for diagnosing "why aren't users paying" on a launch. */}
      <section
        className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C9A227]/40 rounded-sm p-5 mb-8"
        data-testid="reconcile-section"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#C9A227]" />
              فحص الطلبات المعلّقة (Pending) مقابل Stripe
            </h2>
            <div className="text-[11px] text-[#A1B0C6] mt-1 leading-relaxed">
              يقارن كل طلب معلّق في هذا التبويب مع Stripe لكشف:
              <span className="text-[#5EEAD4]"> دفعات ناجحة لم تُسجّل</span>،
              <span className="text-[#FBBF24]"> فتح صفحة الدفع لكن لم يُدخل البطاقة</span>،
              <span className="text-[#F87171]"> بطاقات رُفضت فعلياً (تظهر في خانة decline_code)</span>.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => reconcilePending({ fix: false })}
              disabled={reconciling}
              data-testid="reconcile-dry-run-btn"
              className="text-xs px-3 py-2 border border-[#C9A227]/40 text-[#C9A227] hover:bg-[#C9A227]/10 rounded-sm disabled:opacity-50"
            >
              {reconciling ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "تشغيل الفحص (بدون تعديل)"}
            </button>
            <button
              onClick={() => reconcilePending({ fix: true })}
              disabled={reconciling}
              data-testid="reconcile-fix-btn"
              className="text-xs px-3 py-2 bg-[#C9A227] text-[#0F172A] hover:bg-[#b78d1b] font-bold rounded-sm disabled:opacity-50"
            >
              فحص + استرداد المدفوعات الصامتة
            </button>
          </div>
        </div>

        {reconcileReport && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-center mb-4" data-testid="reconcile-report">
            {[
              { k: "total", label: "إجمالي معلّق", color: "text-white" },
              { k: "succeeded", label: "مدفوع غير مسجّل", color: "text-[#5EEAD4]" },
              { k: "no_pi", label: "لم يفتح الدفع", color: "text-[#A1B0C6]" },
              { k: "requires_payment_method", label: "فتح الدفع — لم يدخل بطاقة", color: "text-[#FBBF24]" },
              { k: "requires_action", label: "3DS غير مكتمل", color: "text-[#FBBF24]" },
              { k: "canceled", label: "ملغى", color: "text-[#A1B0C6]" },
              { k: "error", label: "خطأ Stripe API", color: "text-[#F87171]" },
            ].map((c) => (
              <div key={c.k} className="bg-[#040914]/60 border border-[#C5A059]/15 rounded-sm p-3">
                <div className={`font-display text-2xl font-black ${c.color}`}>
                  {fmt(reconcileReport.summary?.[c.k] || 0)}
                </div>
                <div className="text-[10px] text-[#A1B0C6] mt-1">{c.label}</div>
              </div>
            ))}
          </div>
        )}

        {reconcileReport && (reconcileReport.country_breakdown || []).length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] text-[#A1B0C6] uppercase tracking-wider mb-2">حسب الدولة</div>
            <div className="flex flex-wrap gap-2">
              {reconcileReport.country_breakdown.map(([code, n]) => (
                <span key={code} className="bg-[#040914] border border-[#C5A059]/20 px-2.5 py-1 rounded-sm text-xs text-[#A1B0C6]">
                  {code} · <span className="text-white font-bold">{n}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {reconcileReport && (reconcileReport.decline_codes || []).length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] text-[#F87171] uppercase tracking-wider mb-2">أسباب رفض البطاقة (decline_code)</div>
            <div className="flex flex-wrap gap-2">
              {reconcileReport.decline_codes.map(([code, n]) => (
                <span key={code} className="bg-[#F87171]/10 border border-[#F87171]/30 px-2.5 py-1 rounded-sm text-xs text-[#F87171]">
                  {code} · <span className="text-white font-bold">{n}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {reconcileReport && reconcileReport.fix && (reconcileReport.fixed_orders || []).length > 0 && (
          <div className="mt-4 bg-[#0F766E]/10 border border-[#0F766E]/40 rounded-sm p-3 text-sm text-[#5EEAD4]" data-testid="reconcile-fixed-list">
            ✓ تم استرداد {reconcileReport.fixed_orders.length} طلب — تحوّلوا إلى Paid وتم تشغيل البريد وأحداث TikTok
          </div>
        )}
      </section>

      {/* CTA breakdown — per-button click counts */}
      {Array.isArray(stats.cta_breakdown) && stats.cta_breakdown.length > 0 && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-5 mb-8"
          data-testid="cta-breakdown-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">نقرات أزرار الشراء حسب الموقع</h2>
            <span className="text-xs text-[#A1B0C6]">{fmt(stats.cta_clicks || 0)} نقرة</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {stats.cta_breakdown.map((row) => (
              <div
                key={row.label}
                className="bg-[#040914]/60 border border-[#C5A059]/15 rounded-sm p-3 text-center"
                data-testid={`cta-row-${row.label}`}
              >
                <div className="font-display text-2xl font-black text-[#C5A059]">{fmt(row.count)}</div>
                <div className="text-[11px] tracking-[0.15em] text-[#A1B0C6] mt-1 uppercase">{row.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Chat widget metrics (Gulf / Thabit funnel) */}
      {segment === "thabit" && (stats.chat_widget_opens > 0 || stats.chat_sessions_total > 0) && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#25D366]/40 rounded-sm p-5 mb-8"
          data-testid="chat-analytics-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#25D366]" />
              دردشة «تحدث معنا» (Gulf)
            </h2>
            <span className="text-xs text-[#A1B0C6]">واتساب — Gulf فقط</span>
          </div>

          {(() => {
            const opens = stats.chat_widget_opens || 0;
            const sessions = stats.chat_sessions_total || 0;
            const msgsUser = stats.chat_messages_total || 0;
            const ctaTotal = stats.chat_cta_total || 0;
            const ctr = opens > 0 ? Math.round((ctaTotal / opens) * 100) : 0;
            return (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="chat-opens-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">فتحات الزرّ</span>
                    <MessageSquare className="w-4 h-4 text-[#25D366]" strokeWidth={1.5} />
                  </div>
                  <div className="font-display text-2xl font-black text-white">{fmt(opens)}</div>
                </div>
                <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="chat-sessions-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">جلسات الدردشة</span>
                    <Users className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                  </div>
                  <div className="font-display text-2xl font-black text-white">{fmt(sessions)}</div>
                </div>
                <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="chat-msgs-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">رسائل من العميل</span>
                    <Mail className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                  </div>
                  <div className="font-display text-2xl font-black text-white">{fmt(msgsUser)}</div>
                  <div className="text-[11px] text-[#A1B0C6] mt-1">
                    {sessions > 0 ? (msgsUser / sessions).toFixed(1) : 0} / جلسة
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#25D366]/18 via-[#25D366]/8 to-transparent border border-[#25D366]/40 rounded-sm p-4" data-testid="chat-cta-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">نقرات CTA</span>
                    <MousePointerClick className="w-4 h-4 text-[#25D366]" strokeWidth={1.5} />
                  </div>
                  <div className="font-display text-2xl font-black text-[#25D366]">{fmt(ctaTotal)}</div>
                  <div className="text-[11px] text-[#A1B0C6] mt-1">روابط داخل الدردشة</div>
                </div>
                <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="chat-ctr-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">معدل التحويل</span>
                    <TrendingUp className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                  </div>
                  <div className="font-display text-2xl font-black text-[#C5A059]">{ctr}%</div>
                  <div className="text-[11px] text-[#A1B0C6] mt-1">CTA / فتح</div>
                </div>
              </div>
            );
          })()}

          {Array.isArray(stats.chat_link_breakdown) && stats.chat_link_breakdown.length > 0 && (
            <div data-testid="chat-link-breakdown">
              <div className="text-[11px] tracking-[0.2em] text-[#A1B0C6] mb-3">تفصيل النقرات حسب الوجهة</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {stats.chat_link_breakdown.map((row) => {
                  const arabicMap = {
                    "chat-link-checkout": "الشراء",
                    "chat-link-sample": "العينة",
                    "chat-link-refund": "الاسترجاع",
                    "chat-link-other": "أخرى",
                  };
                  return (
                    <div
                      key={row.label}
                      className="bg-[#040914]/60 border border-[#25D366]/25 rounded-sm p-3 text-center"
                      data-testid={`chat-link-${row.label}`}
                    >
                      <div className="font-display text-xl font-black text-[#25D366]">{fmt(row.count)}</div>
                      <div className="text-[11px] text-[#A1B0C6] mt-1">{arabicMap[row.label] || row.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Pink Book chat widget — hidden in 2-tab layout (Pink no longer a product) */}
      {false && (stats.pink_chat_opens > 0 || stats.pink_chat_sessions > 0) && (
        <section
          className="max-w-7xl mx-auto bg-gradient-to-br from-[#8D6E79]/15 via-[#0B1D3A]/70 to-[#0B1D3A]/70 border border-[#D8B7BE]/35 rounded-sm p-5 mb-8"
          data-testid="pink-chat-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg flex items-center gap-2 text-white">
              <span className="text-xl">🎀</span>
              المحادثات (الكتاب الوردي)
            </h2>
            <span className="text-[10px] tracking-[0.2em] text-[#D8B7BE]">PINK CHAT FUNNEL</span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="bg-[#8D6E79]/15 border border-[#D8B7BE]/40 rounded-sm p-4" data-testid="pink-chat-opens-card">
              <div className="text-xs text-[#D8B7BE] tracking-[0.15em] mb-1">فُتحت المحادثة</div>
              <div className="font-display text-3xl font-black text-white">{fmt(stats.pink_chat_opens || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">زائرة ضغطت أيقونة الواتساب</div>
            </div>
            <div className="bg-[#8D6E79]/15 border border-[#D8B7BE]/40 rounded-sm p-4" data-testid="pink-chat-sessions-card">
              <div className="text-xs text-[#D8B7BE] tracking-[0.15em] mb-1">جلسات بدأت</div>
              <div className="font-display text-3xl font-black text-white">{fmt(stats.pink_chat_sessions || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">محادثات مسجّلة في الـ DB</div>
            </div>
            <div className="bg-[#8D6E79]/15 border border-[#D8B7BE]/40 rounded-sm p-4" data-testid="pink-chat-messages-card">
              <div className="text-xs text-[#D8B7BE] tracking-[0.15em] mb-1">رسائل مرسلة</div>
              <div className="font-display text-3xl font-black text-white">{fmt(stats.pink_chat_messages || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">من قِبل الزائرات</div>
            </div>
            <div className="bg-gradient-to-br from-[#C8A977]/25 via-[#C8A977]/10 to-transparent border border-[#C8A977]/50 rounded-sm p-4" data-testid="pink-chat-checkouts-card">
              <div className="text-xs text-[#C8A977] tracking-[0.15em] mb-1">شراء من الشات</div>
              <div className="font-display text-3xl font-black text-[#C8A977]">{fmt(stats.pink_chat_checkouts || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">نقرت على رابط /pink من المحادثة</div>
            </div>
          </div>

          {Array.isArray(stats.pink_chat_link_breakdown) && stats.pink_chat_link_breakdown.length > 0 && (
            <div data-testid="pink-chat-link-breakdown">
              <div className="text-xs tracking-[0.2em] text-[#D8B7BE] mb-2">روابط مضغوطة داخل المحادثة</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {stats.pink_chat_link_breakdown.map((row) => {
                  const arabicMap = {
                    "pink-chat-link-checkout": "صفحة الشراء /pink",
                    "pink-chat-link-sample": "العينة",
                    "pink-chat-link-refund": "الاسترجاع",
                    "pink-chat-link-other": "روابط أخرى",
                  };
                  return (
                    <div key={row.label} className="bg-[#040914]/50 border border-[#D8B7BE]/25 rounded-sm p-2.5">
                      <div className="text-base font-bold text-[#D8B7BE]">{fmt(row.count)}</div>
                      <div className="text-[11px] text-[#A1B0C6] mt-1">{arabicMap[row.label] || row.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Video completion funnel — only relevant for Thabit (Gulf video modal) */}
      {segment === "thabit" && (stats.gulf_video_buckets?.plays || 0) > 0 && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C5A059]/30 rounded-sm p-5 mb-8"
          data-testid="video-completion-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-[#C5A059]" />
              نسبة إكمال الفيديو/الصوت (25% · 50% · 75% · 100%)
            </h2>
          </div>

          {[
            { label: "🎙️ ثابت — الفيديو الترويجي", data: stats.gulf_video_buckets, testid: "gulf", accent: "#C5A059" },
          ].filter((row) => (row.data?.plays || 0) > 0).map((row) => (
            <div key={row.testid} className="mb-5 last:mb-0" data-testid={`video-buckets-${row.testid}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-white">{row.label}</span>
                <span className="text-xs text-[#A1B0C6]">{fmt(row.data.plays)} تشغيل</span>
              </div>
              <div className="grid grid-cols-4 gap-2.5">
                {[
                  { key: "25", count: row.data.reached_25, pct: row.data.pct_25 },
                  { key: "50", count: row.data.reached_50, pct: row.data.pct_50 },
                  { key: "75", count: row.data.reached_75, pct: row.data.pct_75 },
                  { key: "100", count: row.data.reached_100, pct: row.data.pct_100 },
                ].map((b) => (
                  <div
                    key={b.key}
                    className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-3 relative overflow-hidden"
                    data-testid={`bucket-${row.testid}-${b.key}`}
                  >
                    <div className="absolute bottom-0 inset-x-0 transition-all" style={{ height: `${Math.min(b.pct, 100)}%`, opacity: 0.18, backgroundColor: row.accent }} />
                    <div className="relative">
                      <div className="text-[11px] tracking-[0.2em] text-[#A1B0C6]">≥ {b.key}%</div>
                      <div className="font-display text-2xl font-black mt-1" style={{ color: row.accent }}>{b.pct}%</div>
                      <div className="text-[11px] text-[#A1B0C6] mt-0.5">{fmt(b.count)} مشاهد</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Book Preview funnel — only relevant for Thabit (preview modal exists in /gulf) */}
      {segment === "thabit" && (() => {
        const rows = Array.isArray(stats.cta_breakdown) ? stats.cta_breakdown : [];
        const previewLinkClicks = rows.find((r) => r.label === "preview")?.count || 0;
        const modalCtaClicks = rows.find((r) => r.label === "preview-modal-cta")?.count || 0;
        const ctr = previewLinkClicks > 0 ? Math.round((modalCtaClicks / previewLinkClicks) * 100) : 0;
        if (previewLinkClicks === 0 && modalCtaClicks === 0) return null;
        return (
          <section
            className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C5A059]/30 rounded-sm p-5 mb-8"
            data-testid="book-preview-analytics-section"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#C5A059]" />
                تحليلات معاينة الكتاب
              </h2>
              <span className="text-xs text-[#A1B0C6]">قمع التحويل من المعاينة إلى الشراء</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4"
                data-testid="preview-link-clicks-card"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] tracking-[0.2em] text-[#A1B0C6]">نقرات رابط المعاينة</span>
                  <Eye className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                </div>
                <div className="font-display text-3xl font-black text-white">{fmt(previewLinkClicks)}</div>
                <div className="text-[11px] text-[#A1B0C6] mt-2">من البطل + قسم المميزات</div>
              </div>

              <div
                className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4"
                data-testid="preview-modal-cta-card"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] tracking-[0.2em] text-[#A1B0C6]">نقرات زر «احصل على الكتاب»</span>
                  <MousePointerClick className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                </div>
                <div className="font-display text-3xl font-black text-white">{fmt(modalCtaClicks)}</div>
                <div className="text-[11px] text-[#A1B0C6] mt-2">داخل نافذة المعاينة</div>
              </div>

              <div
                className="bg-gradient-to-br from-[#C5A059]/18 via-[#C5A059]/8 to-transparent border border-[#C5A059]/40 rounded-sm p-4"
                data-testid="preview-ctr-card"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] tracking-[0.2em] text-[#A1B0C6]">معدل التحويل من المعاينة</span>
                  <TrendingUp className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                </div>
                <div className="font-display text-3xl font-black text-[#C5A059]">{ctr}%</div>
                <div className="text-[11px] text-[#A1B0C6] mt-2">
                  {fmt(modalCtaClicks)} / {fmt(previewLinkClicks)} فتحوا المعاينة وضغطوا «احصل على الكتاب»
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Gulf funnel — only show for Thabit segment */}
      {segment === "thabit" && (stats.gulf_video_plays > 0 || (stats.gulf_nav_link_breakdown && stats.gulf_nav_link_breakdown.length > 0)) && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C5A059]/30 rounded-sm p-5 mb-8"
          data-testid="gulf-funnel-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-[#C5A059]" />
              قمع الخليج (الفيديو + روابط القائمة)
            </h2>
            <span className="text-xs text-[#A1B0C6]">/ + /sub-pages</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="gulf-video-plays-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">تشغيلات الفيديو</span>
                <PlayCircle className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-2xl font-black text-white">{fmt(stats.gulf_video_plays || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">«استمع لأول سر»</div>
            </div>
            <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="gulf-video-avg-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">متوسط نسبة المشاهدة</span>
                <TrendingUp className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-2xl font-black text-[#C5A059]">{stats.gulf_video_avg_percent || 0}%</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">{fmt(stats.gulf_video_completes || 0)} مشاهدة كاملة</div>
            </div>
            <div className="bg-gradient-to-br from-[#C5A059]/18 via-[#C5A059]/8 to-transparent border border-[#C5A059]/40 rounded-sm p-4" data-testid="gulf-community-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">إجمالي المجتمع</span>
                <Mail className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-2xl font-black text-[#C5A059]">{fmt(stats.community_total || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">إيميلات (مشتركة بين القمعين)</div>
            </div>
          </div>

          {Array.isArray(stats.gulf_nav_link_breakdown) && stats.gulf_nav_link_breakdown.length > 0 && (
            <div data-testid="gulf-nav-breakdown">
              <div className="text-[11px] tracking-[0.2em] text-[#A1B0C6] mb-3">نقرات روابط القائمة</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
                {stats.gulf_nav_link_breakdown.map((row) => {
                  const arabicMap = {
                    "nav-gulf-home": "الرئيسية",
                    "nav-gulf-about": "ما هو الكتاب",
                    "nav-gulf-sample": "عينة من الكتاب",
                    "nav-gulf-community": "انضم لمجتمعنا",
                    "nav-gulf-refund": "سياسة الاسترجاع",
                    "nav-gulf-contact": "للتواصل",
                  };
                  return (
                    <div key={row.label} className="bg-[#040914]/60 border border-[#C5A059]/15 rounded-sm p-3 text-center" data-testid={`gulf-nav-row-${row.label}`}>
                      <div className="font-display text-xl font-black text-[#C5A059]">{fmt(row.count)}</div>
                      <div className="text-[11px] text-[#A1B0C6] mt-1">{arabicMap[row.label] || row.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* EG funnel — sample-page video + nav-link click breakdown + community */}
      {/* Egypt funnel — hidden in 2-tab layout (EG no longer a tracked product) */}
      {false && (stats.eg_video_plays > 0 || stats.eg_sample_cta_clicks > 0 || (stats.nav_link_breakdown && stats.nav_link_breakdown.length > 0) || stats.community_total > 0) && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C5A059]/30 rounded-sm p-5 mb-8"
          data-testid="eg-funnel-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-[#C5A059]" />
              قمع مصر (الفيديو + المجتمع + روابط القائمة)
            </h2>
            <span className="text-xs text-[#A1B0C6]">/eg + /eg/*</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="eg-video-plays-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">تشغيلات الفيديو</span>
                <PlayCircle className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-2xl font-black text-white">{fmt(stats.eg_video_plays || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">«اسمع أول سر»</div>
            </div>
            <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="eg-video-avg-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">متوسط نسبة المشاهدة</span>
                <TrendingUp className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-2xl font-black text-[#C5A059]">{stats.eg_video_avg_percent || 0}%</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">{fmt(stats.eg_video_completes || 0)} مشاهدة كاملة</div>
            </div>
            <div className="bg-[#040914]/60 border border-[#C5A059]/20 rounded-sm p-4" data-testid="eg-sample-cta-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">CTA «عينة من الكتاب»</span>
                <MousePointerClick className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-2xl font-black text-white">{fmt(stats.eg_sample_cta_clicks || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">شراء + تشغيل فيديو</div>
            </div>
            <div className="bg-gradient-to-br from-[#C5A059]/18 via-[#C5A059]/8 to-transparent border border-[#C5A059]/40 rounded-sm p-4" data-testid="eg-community-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.2em] text-[#A1B0C6]">أعضاء المجتمع</span>
                <Mail className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-2xl font-black text-[#C5A059]">{fmt(stats.community_total || 0)}</div>
              <div className="text-[11px] text-[#A1B0C6] mt-1">إجمالي الإيميلات (كل المصادر)</div>
            </div>
          </div>

          {Array.isArray(stats.community_breakdown) && stats.community_breakdown.length > 0 && (
            <div className="mb-5" data-testid="community-breakdown">
              <div className="text-[11px] tracking-[0.2em] text-[#A1B0C6] mb-3">تفصيل الإيميلات حسب المصدر</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                {stats.community_breakdown.map((row) => {
                  const arabicMap = {
                    "eg-community": "مجتمع مصر",
                    "gulf-community": "مجتمع الخليج",
                    "sample-lead-eg": "عينة (مصر)",
                    "sample-lead-gulf": "عينة (الخليج)",
                    "community": "غير محدد",
                  };
                  const isWarmLead = row.source.startsWith("sample-lead");
                  return (
                    <div
                      key={row.source}
                      className={`rounded-sm p-3 text-center border ${isWarmLead ? "bg-[#C5A059]/8 border-[#C5A059]/30" : "bg-[#040914]/60 border-[#C5A059]/15"}`}
                      data-testid={`community-source-${row.source}`}
                    >
                      <div className="font-display text-xl font-black text-[#C5A059]">{fmt(row.count)}</div>
                      <div className="text-[11px] text-[#A1B0C6] mt-1">{arabicMap[row.source] || row.source}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {Array.isArray(stats.nav_link_breakdown) && stats.nav_link_breakdown.length > 0 && (
            <div data-testid="eg-nav-breakdown">
              <div className="text-[11px] tracking-[0.2em] text-[#A1B0C6] mb-3">نقرات روابط القائمة</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                {stats.nav_link_breakdown.map((row) => {
                  const arabicMap = {
                    "nav-eg-home": "الرئيسية",
                    "nav-eg-about": "ما هو الكتاب",
                    "nav-eg-sample": "عينة من الكتاب",
                    "nav-eg-community": "انضم لمجتمعنا",
                    "nav-eg-refund": "سياسة الاسترجاع",
                    "nav-eg-contact": "للتواصل",
                  };
                  return (
                    <div
                      key={row.label}
                      className="bg-[#040914]/60 border border-[#C5A059]/15 rounded-sm p-3 text-center"
                      data-testid={`nav-row-${row.label}`}
                    >
                      <div className="font-display text-xl font-black text-[#C5A059]">{fmt(row.count)}</div>
                      <div className="text-[11px] text-[#A1B0C6] mt-1">{arabicMap[row.label] || row.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}


      {/* CTA clicks by country */}
      {Array.isArray(stats.cta_country_breakdown) && stats.cta_country_breakdown.length > 0 && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-5 mb-8"
          data-testid="cta-country-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#C5A059]" />
              نقرات CTA حسب الدولة
            </h2>
            <span className="text-xs text-[#A1B0C6]">{stats.cta_country_breakdown.length} دولة</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {stats.cta_country_breakdown.map((row) => (
              <div
                key={row.country}
                className="bg-[#040914]/60 border border-[#C5A059]/15 rounded-sm p-3 text-center"
                data-testid={`country-row-${row.country}`}
              >
                <div className="font-display text-2xl font-black text-[#C5A059]">{fmt(row.count)}</div>
                <div className="text-[11px] tracking-[0.15em] text-[#A1B0C6] mt-1 uppercase">
                  {row.country === "XX" ? "غير معروف" : row.country}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Payment failure reasons */}
      {Array.isArray(stats.failure_reasons) && stats.failure_reasons.length > 0 && (
        <section
          className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-red-500/30 rounded-sm p-5 mb-8"
          data-testid="failure-reasons-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              أسباب فشل الدفع
            </h2>
            <span className="text-xs text-[#A1B0C6]">
              {stats.failure_reasons.reduce((s, r) => s + (r.count || 0), 0)} محاولة فاشلة
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-right text-[#A1B0C6] border-b border-[#C5A059]/15">
                  <th className="p-2 font-medium">السبب</th>
                  <th className="p-2 font-medium">رمز Stripe</th>
                  <th className="p-2 font-medium">العدد</th>
                </tr>
              </thead>
              <tbody>
                {stats.failure_reasons.map((r, i) => (
                  <tr key={i} className="border-b border-[#C5A059]/10" data-testid={`fail-row-${i}`}>
                    <td className="p-2 text-white/90">{r.message || "غير محدد"}</td>
                    <td className="p-2 text-xs font-mono text-[#A1B0C6]">
                      {r.decline_code || r.code || "—"}
                    </td>
                    <td className="p-2 font-bold text-red-300">{fmt(r.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-[#C5A059]/15 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">
            {segment === "ai" ? "طلبات AI Work Upgrade" : "طلبات ثابت (داخلية)"}
          </h2>
          <span className="text-xs text-[#A1B0C6]">{(() => {
            const isAIPkg = (o) => o.package === "ai_work_upgrade" || o.is_ai === true;
            const filtered = orders.filter((o) =>
              segment === "ai" ? isAIPkg(o) : false
            );
            return `${filtered.length} طلب`;
          })()}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="text-right text-[#A1B0C6] border-b border-[#C5A059]/15">
                <th className="p-4 font-medium">المعرّف</th>
                <th className="p-4 font-medium">البريد</th>
                <th className="p-4 font-medium">الباقة</th>
                <th className="p-4 font-medium">المبلغ</th>
                <th className="p-4 font-medium">البلد</th>
                <th className="p-4 font-medium">الحالة</th>
                <th className="p-4 font-medium">سبب الفشل</th>
                <th className="p-4 font-medium">التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const isAIPkg = (o) => o.package === "ai_work_upgrade" || o.is_ai === true;
                // Thabit uses Stripe Payment Link only (no internal orders).
                // AI may have legacy orders from before the Payment Link migration.
                const filtered = orders.filter((o) =>
                  segment === "ai" ? isAIPkg(o) : false
                );
                if (filtered.length === 0) {
                  return (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-[#A1B0C6]">
                        {segment === "thabit"
                          ? "لا توجد طلبات داخلية — كل المبيعات تتم عبر Stripe Payment Link مباشرة. شاهد قسم «زيارات صفحة الدفع» في الأعلى."
                          : "لا توجد طلبات بعد."}
                      </td>
                    </tr>
                  );
                }
                return filtered.map((o) => (
                  <tr key={o.id} className="border-b border-[#C5A059]/10 hover:bg-[#0B1D3A]/40" data-testid={`order-row-${o.id}`}>
                    <td className="p-4 font-mono text-xs text-white/80">{o.id.slice(0,8)}…</td>
                    <td className="p-4 text-xs">
                      {o.email ? (
                        <button
                          onClick={() => { navigator.clipboard?.writeText(o.email); toast.success("نُسخ البريد"); }}
                          className="text-[#C5A059] hover:underline" style={{ direction: "ltr" }}
                          title="انقر للنسخ"
                          data-testid={`order-email-${o.id}`}
                        >
                          {o.email}
                        </button>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="p-4">{o.package_name}</td>
                    <td className="p-4 text-[#C5A059] font-bold">
                      {(o.package || "").startsWith("pdf_audio_eg") || o.provider === "geidea" || o.currency === "EGP"
                        ? `${Math.round(o.display_egp || o.amount)} جنيه`
                        : (o.package === "pink_audio" || o.is_pink)
                          ? `${o.display_sar || 149} ريال · $${o.amount.toFixed(2)}`
                          : (o.package === "ai_work_upgrade" || o.is_ai)
                            ? `$${o.amount.toFixed(2)} USD`
                            : `$${o.amount.toFixed(2)}`}
                    </td>
                    <td className="p-4 text-xs">
                      {o.country && o.country !== "XX" ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-white/5 border border-white/15 text-white/85 font-mono font-bold" data-testid={`order-country-${o.id}`}>
                          {{ SA: "🇸🇦", AE: "🇦🇪", KW: "🇰🇼", EG: "🇪🇬", US: "🇺🇸", GB: "🇬🇧", QA: "🇶🇦", BH: "🇧🇭", OM: "🇴🇲", JO: "🇯🇴", LB: "🇱🇧", IQ: "🇮🇶", DZ: "🇩🇿", MA: "🇲🇦", TN: "🇹🇳", SD: "🇸🇩", LY: "🇱🇾", YE: "🇾🇪", PS: "🇵🇸", SY: "🇸🇾", TR: "🇹🇷", DE: "🇩🇪", FR: "🇫🇷", CA: "🇨🇦", AU: "🇦🇺" }[o.country] || "🌐"}
                          <span>{o.country}</span>
                        </span>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-sm border ${
                          o.status === "Paid"
                            ? "bg-[#C5A059]/10 border-[#C5A059]/40 text-[#C5A059]"
                            : o.status === "Failed"
                              ? "bg-red-500/10 border-red-500/40 text-red-300"
                              : "bg-white/5 border-white/20 text-white/70"
                        }`}
                      >
                        {o.status === "Paid" ? "مدفوع" : o.status === "Failed" ? "فشل" : "بانتظار الدفع"}
                      </span>
                    </td>
                    <td className="p-4 text-xs max-w-[260px]">
                      {o.fail_message ? (
                        <div className="flex flex-col gap-0.5" data-testid={`order-fail-${o.id}`}>
                          <span className="text-red-300 leading-snug">{o.fail_message}</span>
                          {(o.fail_decline_code || o.fail_code) && (
                            <span className="text-[10px] font-mono text-[#A1B0C6]/70" style={{ direction: "ltr" }}>
                              {o.fail_decline_code || o.fail_code}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="p-4 text-xs text-[#A1B0C6]">
                      {new Date(o.created_at).toLocaleString("ar-EG")}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </section>

      <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!resetting) { setResetOpen(o); if (!o) setResetText(""); } }}>
        <AlertDialogContent className="bg-[#0B1D3A] border border-red-500/40 text-white" dir="rtl" data-testid="reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-right text-red-400">
              تحذير — عملية لا يمكن التراجع عنها
            </AlertDialogTitle>
            <AlertDialogDescription className="text-right text-[#A1B0C6] leading-relaxed">
              ستقوم بمسح <span className="text-white font-bold">بيانات مشروع {segment === "thabit" ? "ثابت (Thabit)" : "AI Work Upgrade"} فقط</span>:
              <br/>الطلبات والجلسات والزيارات والتحليلات الخاصة بهذا المشروع.
              <br/><br/>
              <span className="text-[#5EEAD4] text-[12.5px]">المشاريع الأخرى (Reply Rescue / IPO / Performance Ads OS) لن تتأثر.</span>
              <br/><br/>
              <span className="text-red-400 font-bold">هذه العملية نهائية ولا يمكن استرجاع البيانات.</span>
              <br/><br/>
              للتأكيد، اكتب <code className="bg-black/40 text-red-400 px-2 py-0.5 rounded-sm font-mono">RESET</code> في الحقل أدناه:
            </AlertDialogDescription>
          </AlertDialogHeader>

          <Input
            value={resetText}
            onChange={(e) => setResetText(e.target.value)}
            placeholder="اكتب RESET هنا"
            className="bg-[#040914] border-red-500/40 text-white font-mono text-center tracking-widest"
            data-testid="reset-confirm-input"
            disabled={resetting}
            autoFocus
          />

          <AlertDialogFooter className="flex-row-reverse gap-2 sm:gap-2">
            <AlertDialogAction
              asChild
              onClick={(e) => { e.preventDefault(); performReset(); }}
            >
              <Button
                disabled={resetText !== "RESET" || resetting}
                className="bg-red-500 hover:bg-red-600 text-white border-0 disabled:opacity-50"
                data-testid="reset-confirm-btn"
              >
                {resetting ? <><Loader2 className="w-4 h-4 animate-spin ml-2"/> جاري المسح...</> : "نعم، امسح كل البيانات"}
              </Button>
            </AlertDialogAction>
            <AlertDialogCancel
              asChild
              disabled={resetting}
            >
              <Button variant="outline" className="bg-transparent border-[#C5A059]/30 text-[#A1B0C6] hover:bg-[#C5A059]/10 hover:text-white" data-testid="reset-cancel-btn">
                إلغاء
              </Button>
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
